import type { Intent } from '@danda/shared';
import {
  MIN_PM_BALANCE,
  LOW_BALANCE_GATE_TEXT,
  LOW_BALANCE_GATE_BUTTON_LABEL,
} from '@danda/shared';
import { useGameStore } from '@/lib/stores/gameStore';
import { usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import { gameConnectionManager } from '@/lib/game-connection-manager';
import { getStaffAvatarUrl } from '@/lib/media';

/**
 * Submit a fully-structured Intent from a non-typed UI affordance (a scene-panel
 * tap on a pickable item or an interactable object). Mirrors the `player_intent`
 * send path in `ChatPanel` so a tap behaves exactly like the matching slash
 * command: optimistic player bubble, low-balance gate, then the typed-intent WS
 * frame + thinking indicator. No `player_text` / LLM fallback — the FE already
 * has the id, so the backend runs `enrich_intent` directly.
 *
 * `text` is the canonical chat-bubble copy ("Pick up Healing Potion").
 */
export function submitTapIntent(intent: Intent, text: string): void {
  const gameState = useGameStore.getState();
  const charId = gameState.playerCharacterId;
  const char = charId ? gameState.partyMembers.find((c) => c.charId === charId) : undefined;

  // Optimistic echo so the player sees their action immediately (parity with
  // the slash/card flow in ChatPanel:1151 and mobile `sendTypedIntent`).
  gameState.addMessage({
    id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    senderId: charId ?? 'user',
    senderName: char?.name ?? 'You',
    content: text,
    timestamp: new Date(),
    type: 'user',
    avatarUrl: char?.avatarUrl,
    avatarEmoji: char?.avatarEmoji,
  });

  // Low-balance gate — short-circuit the LLM-spending send when the cached PM
  // balance is below MIN_PM_BALANCE, mirroring ChatPanel:1024-1108 and mobile
  // `sendTypedIntent`'s `_shouldGateLowBalance`. Without this a scene-panel tap
  // would bypass the gate that blocks chat/slash sends, desyncing policy + UI.
  // `null` (unknown, pre-REST) is NOT gated to avoid a cold-start flash.
  const cachedBalance = usePmBalanceStore.getState().pmBalance;
  if (cachedBalance !== null && cachedBalance < MIN_PM_BALANCE) {
    gameState.addMessage({
      id: `gate-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      senderId: 'tavian',
      senderName: 'Tavian',
      content: LOW_BALANCE_GATE_TEXT,
      timestamp: new Date(),
      type: 'token-gate',
      avatarUrl: getStaffAvatarUrl('tavian-avatar.webp'),
      meta: {
        action_kind: 'buyTokens',
        action_label: LOW_BALANCE_GATE_BUTTON_LABEL,
      },
    });
    return;
  }

  gameConnectionManager.sendAction({
    type: 'player_intent',
    intent,
    action_slot: 'action',
    scene_id_at_emit: gameState.currentScene?.sceneId,
  });

  useGameStore.setState({ isWaitingForRound: true });
}
